import { searchStore } from "@/shared/store/searchStore/searchStore";
import { WebSocketSearchType } from "./types";
import { alertHandler } from "@/shared/utils/alertHandler";
import { authStore } from "@/shared/store/authStore/authStore";

const WSS_BASE_URL = `${process.env.NEXT_PUBLIC_BACK_PROD_WSS}/patent-search/patents/ws`;
enum StatusLoading {
  "DONE" = 'success',
  "FAIL" = 'error',
}

enum StatusLoadingText {
  "DONE" = 'Завершился поиск по ',
  "FAIL" = 'Ошибка поиска по ',
}

export class WSSearch {
  private socket: WebSocket | null = null;
  private isFinished: boolean = false;
  private allData: WebSocketSearchType = {
    patent: [],
    source_statuses: {
      "espacenet": { status: "IN_PROGRESS_NUMBERS" },  
      "patentscope": { status: "IN_PROGRESS_NUMBERS" },
      "fips": { status: "IN_PROGRESS_NUMBERS" },
      "rospatent": { status: "IN_PROGRESS_NUMBERS" },
      "epatis": { status: "IN_PROGRESS_NUMBERS" },
      "eapo": { status: "IN_PROGRESS_NUMBERS" },
      "orangebook": { status: "IN_PROGRESS_NUMBERS" },
      "TRANSLATOR": { status: "IN_PROGRESS_NUMBERS" }
    },
  };
  private buffer: Uint8Array = new Uint8Array(0);
  isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }
  connect(
    sessionId: string,
    type: string,
    document_ids: number[],
    onProgress: (progress: number) => void,
    onDataComplete: (data: WebSocketSearchType) => void,
    onChunk?: (chunkData: WebSocketSearchType) => void,
    onError?: (errorText?: string) => void
  ) {
    searchStore.sync.setWebSocketSession(Number(sessionId));
    const token = sessionStorage.getItem("accessToken") || '';
    this.socket = new WebSocket(`${WSS_BASE_URL}`, [token]);
    searchStore.sync.setLoadings(true, 'searchDataLoading');
    alertHandler.addAlert({ status: 'info', defaultText: 'Запуск поиска' });
    this.socket.binaryType = 'arraybuffer'; 
    searchStore.sync.setNeedUpdate(null);
    searchStore.sync.setStartUpdate(false);
    this.isFinished = false;
    this.socket.onopen = () => {
      this.notify(true);
      searchStore.sync.setErrorSearch(false);
      this.socket?.send(JSON.stringify({
        username: authStore.state.userInfo?.username || '',
        search_mode: type,
        session_id: Number(sessionId) || 0,
      }));
    };

    this.socket.onmessage = (event) => {
      try {
        if (typeof event.data === 'string') {
          this.handleTextMessage(event.data, onProgress, onChunk);
        } else if (event.data instanceof ArrayBuffer) {
          this.handleBinaryMessage(event.data, onProgress, onChunk);
        }
      } catch (error) {
        console.error("Error processing message:", error);
        if (onError) onError("Error processing message");
      }
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket error: ", error);
      alertHandler.addAlert({ status: 'error', defaultText: `Сессия ${searchStore.state.activeSessions.find((item) => item.id.toString() === sessionId)?.combination_mnn || ''} была прервана` });
      this.disconnect(true);
      searchStore.sync.setLoadings(false, 'searchDataLoading');
      if (onError) onError("WebSocket error");
    };

    this.socket.onclose = async (event) => {
      this.notify(false);
      searchStore.sync.setWebSocketSession(Number(-1));
      searchStore.sync.setLoadings(false, 'searchDataLoading');
      if(event.code === 1006){
        this.disconnect(true);
      }else
        if (event.code === 1008) {
          try {
            this.connect(sessionId, type, document_ids, onProgress, onDataComplete, onChunk, onError);
          } catch (refreshError) {
            if (onError) onError("Reconnect failed");
          }
        } else {
          this.disconnect();
          searchStore.sync.setLoadings(false, 'searchDataLoading');
          onDataComplete(this.allData);
        }
    };
  }

  private handleTextMessage(data: string, onProgress: (progress: number) => void, onChunk?: (chunkData: WebSocketSearchType) => void) {
    try {
      const parsed = JSON.parse(data);
      this.processParsedData(parsed, onProgress, onChunk);
    } catch (error) {
      console.error("Ошибка парсинга!!!", error);
    }
  }

  private handleBinaryMessage(data: ArrayBuffer, onProgress: (progress: number) => void, onChunk?: (chunkData: WebSocketSearchType) => void) {
    const newData = new Uint8Array(data);
    const combined = new Uint8Array(this.buffer.length + newData.length);
    combined.set(this.buffer);
    combined.set(newData, this.buffer.length);
    this.buffer = combined;
    const endMarker = new TextEncoder().encode("<END>");
    let endIndex = -1;
    
    for (let i = 0; i <= this.buffer.length - endMarker.length; i++) {
      let match = true;
      for (let j = 0; j < endMarker.length; j++) {
        if (this.buffer[i + j] !== endMarker[j]) {
          match = false;
          break;
        }
      }
      if (match) {
        endIndex = i;
        break;
      }
    }

    if (endIndex !== -1) {
      const messageBytes = this.buffer.slice(0, endIndex);
      this.buffer = this.buffer.slice(endIndex + endMarker.length);

      try {
        const decoder = new TextDecoder('utf-8');
        const messageText = decoder.decode(messageBytes);
        const parsed = JSON.parse(messageText);
        const prepareData = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
        this.processParsedData(prepareData, onProgress, onChunk);
        
        if (this.buffer.length > 0) {
          this.handleBinaryMessage(new ArrayBuffer(0), onProgress, onChunk);
        }
      } catch (error) {
        console.error("Ошибка декодирования!!!!!!", error);
      }
    }
  }

  private showAlert(sourceName: string, status: 'DONE' | 'FAIL') {
    alertHandler.addAlert({
      status: StatusLoading[status],
      defaultText: `${StatusLoadingText[status]} ${sourceName}`
    });
  }

  private processParsedData(parsed: any, onProgress: (progress: number) => void, onChunk?: (chunkData: WebSocketSearchType) => void) {
    if (parsed) {
      const patentsData = parsed.patent || [];
      const sourcesData = parsed.source_statuses || this.allData.source_statuses;
      this.isFinished = parsed.is_finished;
      if (this.allData.source_statuses) {
        const sources = [
          { key: 'fips', name: 'FIPS' },
          { key: 'eapo', name: 'EAPO' },
          { key: 'orangebook', name: 'Orange Book' },
          { key: 'patentscope', name: 'Patentscope' },
          { key: 'espacenet', name: 'Espacenet' },
          { key: 'rospatent', name: 'Rospatent' },
          { key: 'epatis', name: 'Eapatis' },
          { key: 'TRANSLATOR', name: 'Translator' },
        ] as const;
        sources.forEach(({ key, name }) => {
          const newStatus = sourcesData[key]?.status;
          const oldStatus = this.allData.source_statuses[key]?.status;
        
          if (newStatus !== oldStatus && (newStatus !== 'IN_PROGRESS_NUMBERS' || newStatus !== 'IN_PROCESS_ANALYSIS')) {
            if (newStatus === 'DONE' || newStatus === 'FAIL') {
              this.showAlert(name, newStatus);
            }
          }
        });
      }
      if(parsed.error){
        alertHandler.addAlert({ status: 'error', defaultText: parsed.error });
      }
      this.allData = {
        patent: [...this.allData.patent, ...(Array.isArray(patentsData) ? patentsData : [patentsData])],
        source_statuses: sourcesData
      };

      if (parsed.progress) {
        onProgress(parsed.progress);
      }

      if (onChunk) {
        onChunk(this.allData);
      }
    }
  }

  disconnect(isError?: boolean) {
    if (this.socket) {
      if(isError){
        alertHandler.addAlert({ status: 'error', defaultText: 'Ошибка соединения! Поиск не был завершен!' });
        searchStore.sync.setErrorSearch(true);
      }else{
        if(this.isFinished){
          alertHandler.addAlert({ status: 'info', defaultText: 'Поиск завершен!' });
          searchStore.sync.setErrorSearch(false);
        }else{
          alertHandler.addAlert({ status: 'warning', defaultText: 'Произошла ошибка поиска!' });
          searchStore.sync.setErrorSearch(false);
        }
      }
      this.notify(false);
      searchStore.sync.setWebSocketSession(Number(-1));
      this.socket.close();
      this.socket = null;
      searchStore.sync.setLoadings(false, 'searchDataLoading');
      this.allData = {
        patent: [],
        source_statuses: {
          "espacenet": { status: "IN_PROGRESS_NUMBERS" },  
          "patentscope": { status: "IN_PROGRESS_NUMBERS" },
          "fips": { status: "IN_PROGRESS_NUMBERS" },
          "rospatent": { status: "IN_PROGRESS_NUMBERS" },
          "epatis": { status: "IN_PROGRESS_NUMBERS" },
          "eapo": { status: "IN_PROGRESS_NUMBERS" },
          "orangebook": { status: "IN_PROGRESS_NUMBERS" },
          "TRANSLATOR": { status: "IN_PROGRESS_NUMBERS" }
        },
      };
      this.buffer = new Uint8Array(0);
    
    }
  }
  private listeners: Array<(connected: boolean) => void> = [];

  subscribe(listener: (connected: boolean) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify(connected: boolean) {
    this.listeners.forEach(l => l(connected));
  }
}

export const wsSearch = new WSSearch();
